import React, { useState } from "react";
import * as hrmsService from "../../services/hrmsService";
import LeaveApplyDrawer from "./LeaveApplyDrawer";
import LeaveRequestDrawer from "./LeaveRequestDrawer";
import CompOffDrawer from "./CompOffDrawer";
import { EmptyState, LoadState, PrimaryButton, SecondaryButton } from "./hrComponents";
import { cardStyle, formatDays, formatRange, statusBadge, statusLabel, tdStyle, thStyle, useLoad } from "./hrUi";

// The caller's own leave: balances for the year, then their requests. Used on My Space and
// on the Leave screen.
export function BalanceCards({ balances }) {
  if (!balances.length) return <EmptyState title="No leave types yet" text="HR has not set up a leave policy for you. Ask HR." />;
  return (
    <div className="d-flex flex-wrap gap-3">
      {balances.map((b) => (
        <div key={b.leave_type_id} style={{ ...cardStyle, padding: "14px 18px", minWidth: 170, borderTop: `3px solid ${b.color || "#C9194A"}` }}>
          <div style={{ fontSize: 13, color: "#6B7280" }}>{b.name}</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: "#111827" }}>{Number(b.available)}</div>
          <div style={{ fontSize: 12, color: "#6B7280" }}>
            available{Number(b.pending) ? ` · ${formatDays(b.pending)} requested` : ""}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function LeaveMy() {
  const bal = useLoad(() => hrmsService.getLeaveBalance(), []);
  const reqs = useLoad(() => hrmsService.listLeaveRequests({ mine: 1 }), []);
  const [applying, setApplying] = useState(false);
  const [resubmit, setResubmit] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [openId, setOpenId] = useState(null);

  const balances = bal.data?.balances || [];
  const rows = reqs.data || [];
  const refresh = () => {
    bal.reload();
    reqs.reload();
  };

  return (
    <>
      <div className="d-flex flex-wrap gap-2 mb-3">
        <PrimaryButton onClick={() => setApplying(true)} disabled={!balances.length}>
          Apply for leave
        </PrimaryButton>
        {balances.some((b) => b.is_comp_off) ? <SecondaryButton onClick={() => setClaiming(true)}>Claim comp-off</SecondaryButton> : null}
      </div>

      <LoadState loading={bal.loading} error={bal.error} onRetry={bal.reload} rows={2}>
        <BalanceCards balances={balances} />
        {bal.data ? (
          <div style={{ fontSize: 12, color: "#6B7280", marginTop: 8 }}>
            Leave year {formatRange(bal.data.year_start, bal.data.year_end)}
          </div>
        ) : null}
      </LoadState>

      <h6 className="mt-4 mb-2">My requests</h6>
      <LoadState loading={reqs.loading} error={reqs.error} onRetry={reqs.reload}>
        {rows.length === 0 ? (
          <EmptyState title="No leave requests yet" />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 520 }}>
              <thead>
                <tr>
                  {["Type", "Dates", "Days", "Status", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={tdStyle}>{r.leave_type_name}</td>
                    <td style={tdStyle}>{formatRange(r.from_date, r.to_date)}</td>
                    <td style={tdStyle}>{Number(r.total_days)}</td>
                    <td style={tdStyle}>
                      <span style={statusBadge(r.status)}>{statusLabel(r.status)}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      <button type="button" className="btn btn-link btn-sm" onClick={() => setOpenId(r.id)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>

      <LeaveApplyDrawer show={applying || !!resubmit} onHide={() => { setApplying(false); setResubmit(null); }} onDone={refresh} balances={balances} resubmit={resubmit} />
      <LeaveRequestDrawer requestId={openId} onClose={() => setOpenId(null)} onChanged={refresh} onResubmit={setResubmit} />
      <CompOffDrawer show={claiming} onHide={() => setClaiming(false)} onDone={refresh} />
    </>
  );
}
